import type { Optional } from '@festina-life/core'
import { Button, ButtonContainer } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { Modal } from '@festina-life/ui/structure'
import { useEffect, useState } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { useNavigationParams } from '../../system/useNavigationParams'
import { FPlusScopeTabs } from '../fplusScope/FPlusScopeTabs'
import { SimplePath } from '../path/SimplePath'
import { TemplatePath } from '../path/TemplatePath'
import { FPlusEditor } from './FPlusEditor'
import type { FPlusEditorProps } from './types'

type Props = FPlusEditorProps & {
  onClose: () => void
  originalCode: Optional<string>
}

export const FPlusEditorModal = ({
  code, // Code that may have been formatted by the formatting service.
  originalCode,
  fallbackCode,
  onChangeCode,
  expressionPath,
  onClose,
  type,
  simulatedFunctionDeclaration,
}: Props) => {
  const translate = useTranslate()
  const initialCode = originalCode || code || fallbackCode || ''
  const [editedCode, setEditedCode] = useState(initialCode)
  const { onChangeSearchActive } = useConfigurationContext()
  const { layer, block } = useNavigationParams()

  const handleAcceptChanges = () => {
    onChangeCode?.({ fplusSource: editedCode })
  }

  useEffect(() => {
    onChangeSearchActive(false)

    return () => {
      onChangeSearchActive(true)
    }
  }, [onChangeSearchActive])

  return (
    <Modal isOpen onClose={onClose} panelClassName="w-full">
      <Modal.Title
        className="text-base!"
        title={
          ['library', 'template'].includes(type) ? (
            <TemplatePath
              block={block}
              layer={layer}
              value={expressionPath}
              variant="normal"
              shouldRenderTemplateId
              isInteractive={false}
            />
          ) : (
            <SimplePath value={expressionPath} type={type} />
          )
        }
      />
      <Modal.Body outerClassName="max-h-full w-[calc(100vw-100px)] overflow-visible!">
        <FPlusEditor
          code={initialCode}
          expressionPath={expressionPath}
          onChangeCode={setEditedCode}
          prefixFunctionDeclaration={simulatedFunctionDeclaration}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="w-full">
          <ButtonContainer className="w-full flex-row-reverse">
            <Button variant="discreet" onClick={onClose}>
              {translate('close')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAcceptChanges}
              disabled={editedCode === initialCode}
            >
              {translate('save')}
            </Button>
          </ButtonContainer>
          <FPlusScopeTabs
            expressionPath={expressionPath}
            tabPanelClassName="h-[30vh]! overflow-x-auto overflow-y-hidden w-full"
          />
        </div>
      </Modal.Footer>
    </Modal>
  )
}
